import { PortalUser } from '@/common/components/ecorr-store/portal-user/portal-user';
import { portalUserSelector } from '@/common/components/ecorr-store/portal-user/portal-user.selector';
import { PortalUserState } from '@/common/components/ecorr-store/portal-user/portal-user.state';
import { ParadataEventService } from '@/common/paradata-event/paradata-event.service';
import { FileUtilService } from '@/common/utils/file-util.service';
import { SurveyViewsBaseComponent } from '@/surveys/components/survey-views-base/survey-views-base.component';
import { SurveyViewsBaseService } from '@/surveys/services/survey-views-base.service';
import { AddSurveyDialogService } from '@/surveys/services/add-survey-dialog.service';
import { EnableCookiesDialogService } from '@/surveys/services/enable-cookies-dialog.service';
import { SurveySelectionDialogService } from '@/surveys/services/survey-selection-dialog.service';
import { SurveySelectionDialogData } from '@/surveys/dialogs-data/survey-selection-dialog-data';
import { NotificationOptDialogService } from '@/surveys/services/notification-opt-dialog.service';
import { NotificationOptDialogHttpService } from '@/surveys/services/notification-opt-dialog-http.service';
import { WelcomeSurveyDialogService } from '@/surveys/services/welcome-survey-dialog.service';
import { WelcomeSurveyDialogData } from '@/surveys/dialogs-data/welcome-survey-dialog-data';
import { ExportSurveyDialogData } from '@/surveys/dialogs-data/export-survey-dialog-data';
import { ExportSurveyDialogService } from '@/surveys/services/export-survey-dialog.service';
import { EventService } from '@/surveys/services/event.service';
import { DatePipe } from '@angular/common';
import { Component, OnChanges, OnDestroy, OnInit, Input, inject, signal, computed, DestroyRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppSettings, AppSettingsService } from '@/common/services/app-settings.service';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DecodeXmlModule } from '@/surveys/pipe/decode-xml/decode-xml.module';
import { CalendarViewInstanceComponent } from './calendar-view-instance/calendar-view-instance.component';

interface YearViewData {
    year: number,
    months: MonthData[]
}

interface MonthData {
    date: Date,
    surveyCounter: number,
    status: number, // 9 | 1 | 2 | 3, //  'none' | 'incomplete' | 'overdue' | 'complete',
    statusString: string,
    toolTip: string,
    isActive: boolean,
    isActiveMonth: boolean
}

@Component({
    selector: 'app-calendar-view',
    templateUrl: './calendar-view.component.html',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        MatInputModule,
        MatTabsModule,
        MatIconModule,
        MatListModule,
        MatButtonModule,
        MatCardModule,
        MatTooltipModule,
        MatFormFieldModule,
        DecodeXmlModule,
        CalendarViewInstanceComponent
    ],
    providers: [
        AddSurveyDialogService,
        EnableCookiesDialogService,
        SurveySelectionDialogService,
        ParadataEventService,
        SurveyViewsBaseService,
        NotificationOptDialogService,
        NotificationOptDialogHttpService,
        WelcomeSurveyDialogService,
        ExportSurveyDialogService,
        EventService,
        FileUtilService
    ]
})
export class CalendarViewComponent extends SurveyViewsBaseComponent implements OnInit, OnDestroy, OnChanges {
    // Services using inject()
    private appSettingsService = inject(AppSettingsService);
    private surveySelectionDialogService = inject(SurveySelectionDialogService);
    private exportSurveyDialogService = inject(ExportSurveyDialogService);
    private datePipe = inject(DatePipe);
    protected destroyRef = inject(DestroyRef);
    protected store = inject(Store<PortalUserState>);
    protected eventService = inject(EventService);
    protected paradataEventService = inject(ParadataEventService);
    protected enableCookiesDialogService = inject(EnableCookiesDialogService);

    // Signals for reactive state
    settings = signal<AppSettings>({ 
        pegaIntegration: true
    });
    user = signal<PortalUser | null>(null);
    dateList = signal<any[]>([]);
    activeMonth = signal<Date | null>(null);
    nextMonth = signal<Date | null>(null);
    exporting = signal(false);
    yearViewData = signal<YearViewData[]>([]);
    calMonthStart = signal<Date | null>(null);
    calMonthEnd = signal<Date | null>(null);

    // Computed signals
    currentSettings = computed(() => this.settings());
    currentUser = computed(() => this.user());
    currentDateList = computed(() => this.dateList());
    currentActiveMonth = computed(() => this.activeMonth());
    currentNextMonth = computed(() => this.nextMonth());
    isExporting = computed(() => this.exporting());
    currentYearViewData = computed(() => this.yearViewData());
    currentCalMonthStart = computed(() => this.calMonthStart());
    currentCalMonthEnd = computed(() => this.calMonthEnd());

    // Properties that will be set by parent components
    @Input() surveys: any[] = [];
    @Input() active: boolean = false;
    @Input('overdue-message') overdueMessage: string = '';

    // Legacy subscriptions for compatibility
    event$: Subscription;
    user$: Subscription;

    constructor() {
        super();
        this.user$ = this.store.select(portalUserSelector)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
                user => {
                    this.user.set(user);
                }
            );

        this.appSettingsService.getAppSettings()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe( settings => {
                if ( typeof settings === 'string' ) {
                      // Manually retyping to string then removing tainting and reparsing
                    this.settings.set(JSON.parse( JSON.stringify( settings ).slice( 8, -1 ).replace( /\\/g, '' ) ));
                }
            });
    }


    // Build both given month and next month
    buildCalendars(date: Date) {
        let currentDateList = this.dateList();
        if (!currentDateList || !Array.isArray(currentDateList)) {
            currentDateList = [[], []];
        }
        currentDateList[0] = this.buildCalendar(date);
        const nextMonth = new Date(date.getTime());
        nextMonth.setMonth(date.getMonth() + 1);
        this.nextMonth.set(nextMonth);
        currentDateList[1] = this.buildCalendar(nextMonth);
        this.dateList.set([...currentDateList]);
    }

    // Build the calendar object that runs the calendar
    buildCalendar(date: Date) {
        const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);

        // Determine total number of days visible in calendar
        // Day of the week of first day (number of days needed to pad left of first week) + days in current month + whatever is needed to bring total to a number evenly divisible by 7
        let sizeOfCalendar = firstDayOfMonth.getDay() + lastDayOfMonth.getDate();
        // Add enough to fill out the last week
        if (sizeOfCalendar % 7 !== 0) {
            sizeOfCalendar += 7 - (sizeOfCalendar % 7);
        }

        // Iterate over full calendar size, starting with left padded first day of current month through right padded last day of current month
        const dateIter = new Date(firstDayOfMonth.getTime());
        dateIter.setDate(firstDayOfMonth.getDate() - firstDayOfMonth.getDay());
        const dateList = [];
        for (let i = 0; i < sizeOfCalendar; i++) {
            const calendarDate = new Date(dateIter.getTime());
            dateList.push({
                isActiveMonth: dateIter.getMonth() === date.getMonth(),
                isWeekend: calendarDate.getDay() === 0 || calendarDate.getDay() === 6,
                date: calendarDate,
                surveys: this.findSurveyForDay(dateIter),
                last: calendarDate.getTime() === lastDayOfMonth.getTime()
            });

            dateIter.setDate(dateIter.getDate() + 1);
        }

        return dateList;
    }

    // Find all active surveys due on given date
    findSurveyForDay(date: Date) {
        const dueSurveys = [];
        if (!date || !this.surveys || !Array.isArray(this.surveys)) {
            return dueSurveys;
        }
        date = this.formatToIsoDay(date);
        this.surveys.forEach((survey) => {
            if (survey && survey.currentDueDate) {
                const zeroedSurveyDate = this.formatToIsoDay(survey.currentDueDate);

                // Must compare dates at the same time of day, 00:00:00
                if (zeroedSurveyDate && zeroedSurveyDate.getTime() === date.getTime()) {
                    dueSurveys.push(survey);
                }
            }
        });

        return dueSurveys;
    }

    monthClicked(monthData: MonthData) {
        if (monthData.isActive) {
            this.activeMonth.set(monthData.date);
            this.buildCalendars(monthData.date);
            this.markActiveMonth();
        }
    }

    markActiveMonth() {
        const currentYearViewData = this.yearViewData();
        const currentActiveMonth = this.activeMonth();
        if (!currentActiveMonth) return;
        
        currentYearViewData.forEach(item => {
            item.months.forEach(month => {
                if (month.date.getTime() === currentActiveMonth.getTime()) {
                    month.isActiveMonth = true;
                } else {
                    month.isActiveMonth = false;
                }
            });
        });
        this.yearViewData.set([...currentYearViewData]);
    }

    changeMonth(operation: string, fast: boolean) {
        let date = new Date(this.activeMonth());
        if (!this.isMonthInRange(date)) {
            return;
        }
        switch (operation) {
            case 'add':
                date.setMonth(date.getMonth() + 1);
                date = this.formatToIsoMonth(date);
                if (fast) {
                    while (!this.monthHasSurvey(date) && this.isMonthInRange(date)) {
                        date.setMonth(date.getMonth() + 1);
                    }
                }
                break;
            case 'subtract':
                date.setMonth(date.getMonth() - 1);
                date = this.formatToIsoMonth(date);
                if (fast) {
                    while (!this.monthHasSurvey(date) && this.isMonthInRange(date)) {
                        date.setMonth(date.getMonth() - 1);
                    }
                }
                break;
            default:
                break;
        }

        this.activeMonth.set(date);
        this.buildCalendars(this.activeMonth());
        this.markActiveMonth();
    }

    hasMoreOlderSurveys() {
        const date = new Date(this.activeMonth());
        date.setMonth(date.getMonth() - 1);
        while (date.getTime() >= this.calMonthStart().getTime()) {
            if (this.monthHasSurvey(date)) {
                return true;
            }
            date.setMonth(date.getMonth() - 1);
        }
        return false;
    }

    hasMoreNewerSurveys() {
        const date = new Date(this.activeMonth());
        date.setMonth(date.getMonth() + 1);
        while (date.getTime() <= this.calMonthEnd().getTime()) {
            if (this.monthHasSurvey(date)) {
                return true;
            }
            date.setMonth(date.getMonth() + 1);
        }
        return false;
    }

    isMonthInRange(month: Date) {
        if (!month) {
            return false;
        }

        if (month.getTime() >= this.calMonthStart().getTime() && month.getTime() <= this.calMonthEnd().getTime()) {
            return true;
        } else if ( month.getTime() >= this.formatToIsoMonth( new Date() ).getTime() && month.getTime() <= this.formatToIsoMonth( new Date() ).getTime() ) {
            return true;
        } else {
            return false;
        }
    }

    monthHasSurvey(date: Date): boolean {
        if (date == null || !this.surveys || !Array.isArray(this.surveys) || this.surveys.length === 0) {
            return false;
        }
        let found = false;
        this.surveys.forEach((item) => {
            if (item && item.currentDueDate) {
                if (this.active && item.statusIndicator !== '4') {
                    if (this.formatToIsoMonth(item.currentDueDate).getTime() === date.getTime()) {
                        found = true;
                    }
                } else if (!this.active && item.statusIndicator === '4') {
                    if (this.formatToIsoMonth(item.currentDueDate).getTime() === date.getTime()) {
                        found = true;
                    }
                }
            }
        });
        return found;
    }

    showInstructions() {
        const options: ExportSurveyDialogData = {
            title: 'Export Due Date to Calendar Instructions',
            subheading: '',
            message: 'After exporting the desired survey(s), an email containing a calendar (.ICS) file will be sent to the email address on your account. <br> To import the .ICS file to your digital calendar, please choose your provider below to view specific instructions.',
            instructionHeading: 'Export Instructions:',
            instructions: [ {platform: 'Gmail', instructionLink: 'https://support.google.com/calendar/answer/37118?hl=en&co=GENIE.Platform%3DDesktop'},
                {platform: 'Outlook', instructionLink: 'https://support.microsoft.com/en-us/office/import-calendars-into-outlook-8e8364e1-400e-4c0f-a573-fe76b5a2d379'},
                {platform: 'iCloud', instructionLink: 'https://support.apple.com/guide/calendar/import-or-export-calendars-icl1023/mac'},
                {platform: 'Yahoo', instructionLink: 'https://help.yahoo.com/kb/new-mail-for-desktop/add-edit-delete-calendar-events-yahoo-mail-sln28062.html'} ],
            cancelButtonLabel: 'OK'
        };
        this.exportSurveyDialogService.open(options);
    }

    generateCalendar(format?: string) {
        let surveyTypes: string[] = this.getSurveyTypes();

        const options: SurveySelectionDialogData = {
            title: 'Update Case Preferences',
            subheading: 'Which Surveys Do You Want to Export?',
            surveys: surveyTypes
        };
        this.surveySelectionDialogService.open(options);
    }

    getSurveyTypes(): string[] {
        let surveyTypes: string[] = [];
        if (!this.surveys || !Array.isArray(this.surveys)) {
            return surveyTypes;
        }
        for(let survey of this.surveys) {
            if (survey && survey.surveyTypeName && surveyTypes.indexOf(survey.surveyTypeName) === -1) {
                surveyTypes.push(survey.surveyTypeName);
            }
        }
        return surveyTypes;
    }

    // Check to see if user is using Safari (it doesn't support exorting)
    safariCheck() {
        // return navigator.userAgent.toLowerCase().includes('safari');
        return false;
    }

    // Function sent to modals from which to emit paradata events. Modals use isolated scopes so any emissions 
    // from them would not be picked up by the ParadataCatcher unless sent from an attached scope like this.
    // Cannot be added to utilityService, despite its widespread use, because services cannot make use of $scope.
    emitParadataEvent(event: any, data: any) {
        // $scope.$emit(event, data);
    }

    ngOnInit(): void {
        if (!navigator.cookieEnabled) {
            this.enableCookiesDialogService.open(this.enableCookiesInfo);
        }
        this.refresh();
    }

    ngOnChanges(changes: any): void {
        if (changes['surveys'] && changes['surveys'].currentValue) {
            this.refresh();
        }
        if (changes['active'] && changes['active'].currentValue !== undefined) {
            this.refresh();
        }
    }

    refresh(): void {
        if (!this.surveys || this.surveys.length === 0) {
            return;
        }

        this.updateOldNewDates();

        let date = this.formatToIsoMonth( new Date() );
        if ( date.getTime() > this.calMonthEnd().getTime() ) {
            date = this.calMonthEnd();
        }
        // while ( !this.monthHasSurvey( date ) && this.isMonthInRange( date ) ) {
        //     date.setMonth( date.getMonth() + 1 );
        // }
        // while ( !this.isMonthInRange( date ) ) {
        //     date.setMonth( date.getMonth() - 1 );
        // }
        // while ( !this.monthHasSurvey( date ) && this.isMonthInRange( date ) ) {
        //     date.setMonth( date.getMonth() - 1 );
        // }

        let next;
        let previous;
        let today = new Date();

          // Determine which is the next survey due and the most recent survey due
        this.surveys.forEach( survey => {
            if ( survey.currentDueDate.getTime() > today.getTime() ) {
                  // If the survey is sooner than the current next survey (or there is no current next survey), set next survey to current item
                if ( !next || next.getTime() > survey.currentDueDate.getTime() ) { 
                    next = survey.currentDueDate;
                }
            } else if ( survey.currentDueDate.getTime() < today.getTime() ) {
                  // If the survey is later than the current previous survey (or there is no current previous survey), set previous survey to current item
                if ( !previous || previous.getTime() < survey.currentDueDate.getTime() ) { 
                    previous = survey.currentDueDate;
                }
            }
        });

          // Set active month as the next month a survey is due unless there is a survey due earlier this month
        if ( next && ( !previous || previous.getMonth() !== today.getMonth() ) ) {
            this.activeMonth.set(next);
        } else if ( previous ) {
            this.activeMonth.set(previous);
        }

        this.buildCalendars( this.activeMonth() );
        // this.activeMonth = date;

        this.initYearView();
        this.markActiveMonth();
    }

    updateOldNewDates() {
        this.calMonthStart.set(null);
        this.calMonthEnd.set(null);
        this.surveys.forEach((item) => {
            if (this.calMonthStart() == null || this.calMonthStart().getTime() > item.currentDueDate.getTime()) {
                this.calMonthStart.set(this.formatToIsoMonth(item.currentDueDate));
            }
            if (this.calMonthEnd() == null || this.calMonthEnd().getTime() < item.currentDueDate.getTime()) {
                this.calMonthEnd.set(this.formatToIsoMonth(item.currentDueDate));
            }
        });
    }

    initYearView() {
        const yearViewDataArray: YearViewData[] = [];
        const start = this.calMonthStart().getFullYear();
        const end = this.calMonthEnd().getFullYear();
        for (let year = start; year <= end; year++) {
            const yearData: YearViewData = { year: year, months: [] };
            for (let month = 0; month < 12; month++) {
                const monthData: MonthData = {
                    date: this.formatToIsoMonth(new Date(year, month, 1)),
                    surveyCounter: 0,
                    status: 9,
                    statusString: '',
                    toolTip: null,
                    isActive: false,
                    isActiveMonth: false
                };

                if (monthData.date.getTime() >= this.calMonthStart().getTime() && monthData.date.getTime() <= this.calMonthEnd().getTime()) {
                    monthData.isActive = true;
                }
                yearData.months.push(monthData);
            }
            yearViewDataArray.push(yearData);
        }
        this.yearViewData.set(yearViewDataArray);

        this.surveys.forEach((item) => {

            // count incomplete/overdue/complete surveys
            const date = this.formatToIsoMonth(item.currentDueDate);
            this.yearViewData().forEach(data => {
                data.months.forEach(month => {
                    const text = this.datePipe.transform(month.date, 'MMM yyyy');
                    if (month.date.getTime() === date.getTime()) {
                        month.status = Math.min(month.status, item.formStatus);
                        month.statusString = month.status === 1 ? 'overdue' : month.status === 2 ? 'incomplete' : month.status === 3 ? 'complete' : '';
                        month.surveyCounter++;
                        month.toolTip = month.surveyCounter + ' survey' + (month.surveyCounter > 1 ? 's in ' : ' in ') + text;
                    } else {
                        if (!month.toolTip) {
                            month.toolTip = text;
                        }
                    }
                });
            });
        });
    }

    formatToIsoDay(date: Date): Date {
        if (date == null) {
            return null;
        }
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    formatToIsoMonth(date: Date): Date {
        if (date == null) {
            return null;
        }
        return new Date(date.getFullYear(), date.getMonth(), 1);
    }

    ngOnDestroy() {
        this.paradataEventService.flushParadataevents();
        if (this.user$) {
            this.user$.unsubscribe();
        }
        if (this.event$) {
            this.event$.unsubscribe();
        }
        super.destroy();
    }

}
